// src/App.jsx - Updated with shadcn sidebar integration
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SocketProvider } from './contexts/SocketContext.jsx';
import { AuthProvider, useAuth } from './contexts/AuthContext.jsx';

// Auth components - using our new rebuilt components
import Login from './components/auth/Login';
import PartnerSignup from './components/auth/PartnerSignup';

// Dashboard component
import Dashboard from './components/dashboard/DashboardShadcn.jsx';

// Dashboard components
import AppointmentsShadcn from './components/appointments/AppointmentsShadcn.jsx';
import AppointmentDetailShadcn from './components/appointments/AppointmentDetailShadcn';

// Customer components
import CustomersShadcn from './components/Customers/CustomersShadcn.jsx';
import CustomerDetailsShadcn from './components/Customers/CustomerDetailsShadcn.jsx';

// Invoice components - UPDATED: Use shadcn invoices
import InvoicesShadcn from './components/invoices/InvoicesShadcn.jsx'; // NEW: Use shadcn component
import InvoiceBuilderShadcn from './components/invoices/InvoiceBuilderShadcn.jsx'; // NEW: Use shadcn form

// Other components
import PartnersSectionShadcn from './components/partners/PartnersSectionShadcn.jsx';

// Individual Configuration Section Components
import BusinessSettingsShadcn from './components/configuration/sections/BusinessSettingsShadcn';
import CommunicationsSectionShadcn from './components/configuration/sections/CommunicationsSectionShadcn';
import ServicesSectionShadcn from './components/configuration/sections/ServicesSectionShadcn';
import TeamSectionShadcn from './components/configuration/sections/TeamSectionShadcn';

// Reviews components
import ReviewsShadcn from './components/reviews/ReviewsShadcn';

// Calendar Components
import CalendarComponentsTest from './components/calendar/test/CalendarComponentsTest';
import CalendarShadcn from './components/calendar/CalendarShadcn.jsx';

import WidgetBuilderShadcn from './components/widgets/WidgetBuilderShadcn';

import InvitationAcceptance from './pages/InvitationAcceptance';

// Pipeline/Automation Components
import FlowCanvasShadcn from './components/pipelines/FlowCanvasShadcn';

// UI Components - UPDATED: Use shadcn sidebar
import SidebarShadcn from './components/ui/navigation/Sidebar/SidebarShadcn';
import { Toaster } from './components/ui/toaster';

// Notification System
import NotificationProvider from './components/notifications/NotificationProvider';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-6 h-6 text-emerald-400 animate-pulse">✨</div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// UPDATED: shadcn sidebar layout with proper SidebarInset
const AppLayout = ({ children }) => (
  <SidebarShadcn>
    {children}
  </SidebarShadcn>
);

function App() {
  React.useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  return (
    <Router>
      <SocketProvider>
        <AuthProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/accept-invitation" element={<InvitationAcceptance />} />
            <Route path="/login" element={<Login />} />
            <Route path="/partner-signup" element={<PartnerSignup />} />
            
            {/* Protected routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <AppLayout>
                  <Dashboard />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            {/* Customer routes */}
            <Route path="/customers" element={
              <ProtectedRoute>
                <AppLayout>
                  <CustomersShadcn />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/customers/:customerId" element={
              <ProtectedRoute>
                <AppLayout>
                  <CustomerDetailsShadcn />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            {/* Appointment routes */}
            <Route path="/appointments" element={
              <ProtectedRoute>
                <AppLayout>
                  <AppointmentsShadcn />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/appointments/:appointmentId" element={
              <ProtectedRoute>
                <AppLayout>
                  <AppointmentDetailShadcn />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            {/* Calendar route */}
            <Route path="/calendar" element={
              <ProtectedRoute>
                <AppLayout>
                  <CalendarShadcn />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            {/* Invoice routes - CORRECTED */}
            <Route path="/invoices" element={
              <ProtectedRoute>
                <AppLayout>
                  <InvoicesShadcn />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/invoices/create" element={
              <ProtectedRoute>
                <AppLayout>
                  <InvoiceBuilderShadcn />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/invoices/edit/:invoiceId" element={
              <ProtectedRoute>
                <AppLayout>
                  <InvoiceBuilderShadcn />
                </AppLayout>
              </ProtectedRoute>
            } />

            {/* Reviews route */}
            <Route path="/reviews" element={
              <ProtectedRoute>
                <AppLayout>
                  <ReviewsShadcn />
                </AppLayout>
              </ProtectedRoute>
            } />

            {/* Partner routes */}
            <Route path="/partners" element={
              <ProtectedRoute>
                <AppLayout>
                  <PartnersSectionShadcn />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/widgets" element={
              <ProtectedRoute>
                <AppLayout>
                  <WidgetBuilderShadcn />
                </AppLayout>
              </ProtectedRoute>
            } />

            {/* Pipeline/Automation routes */}
            <Route path="/pipelines" element={
              <ProtectedRoute>
                <AppLayout>
                  <FlowCanvasShadcn />
                </AppLayout>
              </ProtectedRoute>
            } />

            {/* Configuration routes - CORRECTED */}
            <Route path="/configuration" element={<Navigate to="/configuration/business" replace />} />
            <Route
              path="/configuration/business"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <BusinessSettingsShadcn />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuration/communications"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <CommunicationsSectionShadcn />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route 
              path="/configuration/services" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ServicesSectionShadcn />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/configuration/team" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <TeamSectionShadcn />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            {/* CHANGED: Redirect old payment route to integrated business settings */}
            <Route 
              path="/configuration/payments" 
              element={<Navigate to="/configuration/business?section=payments" replace />} 
            />
            
            {/* Test routes */}
            <Route path="/calendar-test" element={
              <ProtectedRoute>
                <AppLayout>
                  <CalendarComponentsTest />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </SocketProvider>
      <Toaster />
      <NotificationProvider position="top-right" />
    </Router>
  );
}

export default App;